import clsx from 'clsx';
import pluralize from 'pluralize';
import React from 'react';

import { User } from '../../../utils/backend-api/currentUser';
import UserAvatar, { AvatarSizes } from '../../User/UserAvatar';
import UserProfileLink from '../../User/UserProfileLink';
import TopicPill from './TopicPill';
import styles from './styles.module.scss';

const AVATAR_DISPLAY_LIMIT = 3;

const seperatebyDelimiter = (totalNumber: number) => {
  return totalNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export default function TopicFollowAndTagModal({
  followers,
  totalFollowers,
  relatedTopics,
}: {
  followers: Array<User>;
  totalFollowers: number;
  relatedTopics: Array<string>;
}) {
  const numberOfFollowersText = `${seperatebyDelimiter(totalFollowers)} ${pluralize(
    'Follower',
    totalFollowers
  )}`;

  const followedBySentence = () => {
    // head contains all the els except the last, tail is the last el. Length 1
    // arrays are special cased
    const [tail, ...head] = followers.slice(0, AVATAR_DISPLAY_LIMIT).reverse();
    // Adding Comma for large number

    // No formating required for a length 1 array
    if (totalFollowers === 1) {
      return <UserProfileLink user={followers[0]}>{followers[0].name}</UserProfileLink>;
    }

    // Follower profile links separated with oxford comma
    if (totalFollowers <= AVATAR_DISPLAY_LIMIT) {
      return head
        .map((follower, i) => [
          i > 0 && ', ',
          <UserProfileLink key={follower.id} user={follower}>
            {follower.name}
          </UserProfileLink>,
        ])
        .concat([
          ', and ',
          <UserProfileLink key={tail.id} user={tail}>
            {tail.name}
          </UserProfileLink>,
        ]);
    }

    // Follower profile links and "other" text separated with oxford comma
    return followers
      .slice(0, AVATAR_DISPLAY_LIMIT)
      .map((follower, i) => [
        i > 0 && ', ',
        <UserProfileLink key={follower.id} user={follower}>
          {follower.name}
        </UserProfileLink>,
      ])
      .concat([`, and ${seperatebyDelimiter(totalFollowers - AVATAR_DISPLAY_LIMIT)} others`]);
  };

  return (
    (relatedTopics.length > 0 || followers.length > 0) && (
      <div className={styles.topicFollowAndTagContainer}>
        {followers.length > 0 && (
          <div
            className={clsx(styles.followersContainer, {
              [styles.followersBorderBottom]: relatedTopics.length > 0,
            })}
          >
            {numberOfFollowersText}
            <div className={styles.listFollowersContainer}>
              <div className={styles.stackedImgContainer}>
                {followers.slice(0, AVATAR_DISPLAY_LIMIT).map((follower, i) => {
                  return (
                    <div key={follower.id} style={{ zIndex: `${AVATAR_DISPLAY_LIMIT - i}` }}>
                      <UserAvatar
                        user={follower}
                        size={AvatarSizes.Small}
                        grow={false}
                        customStyles={styles.avatarBorder}
                      />
                    </div>
                  );
                })}
              </div>
              <div className={styles.followedByText}>Followed by {followedBySentence()}.</div>
            </div>
          </div>
        )}
        {relatedTopics.length > 0 && (
          <div className={styles.relatedTopicsContainer}>
            <div
              className={clsx(styles.relatedTopics, {
                [styles.noFollowers]: followers.length === 0,
              })}
            >
              Related Topics
            </div>
            <div className={styles.relatedTopicPills}>
              {relatedTopics.map((topic) => (
                <TopicPill key={topic} topic={topic} />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  );
}
